import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Alert, InputGroup } from 'react-bootstrap';
import { Link, useNavigate } from "react-router-dom";
import { createUser } from '../../../src/Service/user.service';
import { toast } from "react-toastify";
import { AiOutlineEye as Eye, AiOutlineEyeInvisible as EyeOff } from 'react-icons/ai';
import { FaUser, FaEnvelope, FaLock, FaCheck } from 'react-icons/fa';
import './style.scss';
import Header from '../../components/header/Header';
import HeaderTop from '../../components/HeaderTop/HeaderTop';

const SignUpPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);

    const [formData, setFormData] = useState({
        email: '',
        firstName: '',
        lastName: '',
        password: '',
        confirm_password: '',
    });

    const [errors, setErrors] = useState({
        email: '',
        firstName: '',
        lastName: '',
        password: '',
        confirm_password: '',
        terms: '',
        server: ''
    });

    const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    
    const validatePassword = (password) => {
        return (
            password.length >= 8 &&
            /[A-Z]/.test(password) &&
            /[a-z]/.test(password) &&
            /[0-9]/.test(password) &&
            /[!@#$%^&*(),.?":{}|<>]/.test(password)
        );
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handlePasswordChange = (e) => {
        const { value } = e.target;
        setFormData(prev => ({ ...prev, password: value }));
        setErrors(prev => ({ ...prev, password: '' }));
        
        // Show requirements only when there's input
        setShowPasswordRequirements(value.length > 0);
    };

    const validateForm = () => {
        const newErrors = {
            email: !formData.email ? 'Email is required' : 
                   !validateEmail(formData.email) ? 'Please enter a valid email address' : '',
            firstName: !formData.firstName ? 'First name is required' : '',
            lastName: !formData.lastName ? 'Last name is required' : '',
            password: !formData.password ? 'Password is required' : 
                     !validatePassword(formData.password) ? 
                     'Password must meet all requirements' : '',
            confirm_password: formData.password !== formData.confirm_password ? 'Passwords do not match' : '',
            terms: !termsAccepted ? 'You must accept the terms and conditions' : '',
            server: ''
        };

        setErrors(newErrors);
        return !Object.values(newErrors).some(error => error);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        try {
            const userData = {
                user_firstName: formData.firstName,
                user_lastName: formData.lastName,
                user_email: formData.email,
                user_password: formData.password,
                user_password_value: formData.password
            };
            
            const response = await createUser(userData);
            
            if (response.status === 200 || response.status === 201) {
                toast.success('Registration successful! Redirecting to login...', {
                    autoClose: 2000,
                    onClose: () => navigate('/login')
                });
            } else {
                const errorData = await response.json();
                setErrors(prev => ({ ...prev, server: errorData.message || 'Registration failed' }));
                toast.error(errorData.message || 'Registration failed');
            }
        } catch (error) {
            setErrors(prev => ({ 
                ...prev, 
                server: error.response?.data?.message || 'An error occurred during registration' 
            }));
            toast.error('An error occurred during registration');
            console.error('Registration error:', error);
        } finally {
            setLoading(false);
        }
    };

    const passwordRequirements = [
        { label: '8+ characters', valid: formData.password.length >= 8 },
        { label: '1 uppercase letter', valid: /[A-Z]/.test(formData.password) },
        { label: '1 lowercase letter', valid: /[a-z]/.test(formData.password) },
        { label: '1 number', valid: /[0-9]/.test(formData.password) },
        { label: '1 special character', valid: /[!@#$%^&*(),.?":{}|<>]/.test(formData.password) }
    ];

    return (
        <>
        <HeaderTop/>
        <Container fluid className="signup-container">
            <Row className="justify-content-center align-items-center min-vh-100">
                <Col md={8} lg={6} xl={5} className="py-5">
                    <div className="signup-card shadow-lg rounded-4 overflow-hidden">
                        <div className="signup-header bg-primary text-white p-4 text-center">
                            <h2 className="mb-1 fw-bold">Create Your Account</h2>
                            <p className="mb-0 opacity-75">Join our community today</p>
                        </div>
                        
                        <div className="signup-body p-4 p-md-5">
                            {errors.server && (
                                <Alert variant="danger" className="mb-4">
                                    {errors.server}
                                </Alert>
                            )}

                            <Form onSubmit={handleSubmit}>
                                <Row className="g-3">
                                    <Col md={6}>
                                        <Form.Group controlId="firstName">
                                            <Form.Label>First Name</Form.Label>
                                            <InputGroup hasValidation>
                                                <InputGroup.Text>
                                                    <FaUser />
                                                </InputGroup.Text>
                                                <Form.Control
                                                    type="text"
                                                    name="firstName"
                                                    value={formData.firstName}
                                                    onChange={handleChange}
                                                    isInvalid={!!errors.firstName}
                                                    placeholder="Enter your first name"
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.firstName}
                                                </Form.Control.Feedback>
                                            </InputGroup>
                                        </Form.Group>
                                    </Col>
                                    
                                    <Col md={6}>
                                        <Form.Group controlId="lastName">
                                            <Form.Label>Last Name</Form.Label>
                                            <InputGroup hasValidation>
                                                <InputGroup.Text>
                                                    <FaUser />
                                                </InputGroup.Text>
                                                <Form.Control
                                                    type="text"
                                                    name="lastName"
                                                    value={formData.lastName}
                                                    onChange={handleChange}
                                                    isInvalid={!!errors.lastName}
                                                    placeholder="Enter your last name"
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.lastName}
                                                </Form.Control.Feedback>
                                            </InputGroup>
                                        </Form.Group>
                                    </Col>
                                    
                                    <Col xs={12}>
                                        <Form.Group controlId="email">
                                            <Form.Label>Email Address</Form.Label>
                                            <InputGroup hasValidation>
                                                <InputGroup.Text>
                                                    <FaEnvelope />
                                                </InputGroup.Text>
                                                <Form.Control
                                                    type="email"
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    isInvalid={!!errors.email}
                                                    placeholder="Enter your email"
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.email}
                                                </Form.Control.Feedback>
                                            </InputGroup>
                                        </Form.Group>
                                    </Col>
                                    
                                    <Col xs={12}>
                                        <Form.Group controlId="password">
                                            <Form.Label>Password</Form.Label>
                                            <InputGroup hasValidation>
                                                <InputGroup.Text>
                                                    <FaLock />
                                                </InputGroup.Text>
                                                <Form.Control
                                                    type={showPassword ? "text" : "password"}
                                                    name="password"
                                                    value={formData.password}
                                                    onChange={handlePasswordChange}
                                                    isInvalid={!!errors.password}
                                                    placeholder="Create a password"
                                                />
                                                <Button
                                                    variant="outline-secondary"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    className="password-toggle"
                                                >
                                                    {showPassword ? <EyeOff /> : <Eye />}
                                                </Button>
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.password}
                                                </Form.Control.Feedback>
                                            </InputGroup>
                                            
                                            {showPasswordRequirements && (
                                                <div className="password-requirements mt-2">
                                                    <small className="text-muted">Password must contain:</small>
                                                    <ul className="list-unstyled mb-0">
                                                        {passwordRequirements.map((req, index) => (
                                                            <li key={index} className={req.valid ? "text-success" : "text-muted"}>
                                                                <FaCheck className="me-1" size={10} />
                                                                {req.label}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </Form.Group>
                                    </Col>
                                    
                                    <Col xs={12}>
                                        <Form.Group controlId="confirmPassword">
                                            <Form.Label>Confirm Password</Form.Label>
                                            <InputGroup hasValidation>
                                                <InputGroup.Text>
                                                    <FaLock />
                                                </InputGroup.Text>
                                                <Form.Control
                                                    type={showConfirmPassword ? "text" : "password"}
                                                    name="confirm_password"
                                                    value={formData.confirm_password}
                                                    onChange={handleChange}
                                                    isInvalid={!!errors.confirm_password}
                                                    placeholder="Confirm your password"
                                                />
                                                <Button
                                                    variant="outline-secondary"
                                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                    className="password-toggle"
                                                >
                                                    {showConfirmPassword ? <EyeOff /> : <Eye />}
                                                </Button>
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.confirm_password}
                                                </Form.Control.Feedback>
                                            </InputGroup>
                                        </Form.Group>
                                    </Col>
                                    
                                    <Col xs={12}>
                                        
                                    </Col>
                                    
                                    <Col xs={12} className="mt-4">
                                        <Button
                                            variant="primary"
                                            type="submit"
                                            size="lg"
                                            className="w-100 py-2 fw-bold"
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Creating Account...
                                                </>
                                            ) : (
                                                'Create Account'
                                            )}
                                        </Button>
                                    </Col>
                                    
                                    <Col xs={12} className="text-center mt-3">
                                        <p className="mb-0">
                                            Already have an account?{' '}
                                            <Link to="/login" className="text-primary fw-semibold">Sign In</Link>
                                        </p>
                                    </Col>
                                </Row>
                            </Form>
                        </div>
                    </div>
                </Col>
            </Row>
        </Container>
        
        </>
    );
};

export default SignUpPage;